/**
 * Klartext für die Codes des Türbuchs — Sicherheitsstatus, Sicherungsseite,
 * Raumzuordnungsverfahren, Türzustand, Merkmalsnamen aus dem
 * Sicherheitskonzept, Ja/Nein.
 *
 * ════════════════════════════════════════════════════════════════════════
 * WARUM AN EINER STELLE
 * ════════════════════════════════════════════════════════════════════════
 *
 * `Tuerbuch.tsx` zeigt dieselben Codes wie der PDF-Export. Zwei
 * Übersetzungstabellen für dieselben Codes laufen erfahrungsgemäß nach der
 * dritten Änderung auseinander — deshalb steht die Übersetzung hier an EINER
 * Stelle für das Frontend, gespiegelt (zwei Laufzeiten, kein gemeinsamer
 * Code möglich) in `src/Tuv.Export.Api/Services/TuerKlartext.cs`. Ändert
 * sich ein Wert hier, muss er dort mitgezogen werden — und umgekehrt.
 *
 * ════════════════════════════════════════════════════════════════════════
 * UNBEKANNTE CODES VERSCHWINDEN NICHT
 * ════════════════════════════════════════════════════════════════════════
 *
 * Jede Übersetzungsfunktion fällt für einen Code, der nicht in der Tabelle
 * steht, auf den Rohwert zurück statt auf einen Platzhalter. Ein neuer Code —
 * etwa ein fünfter Sicherheitsstatus — fällt damit sofort auf, statt
 * kommentarlos zu „—" zu werden.
 */

function nachschlagen(
  code: string | null | undefined,
  tabelle: Record<string, string>,
  leer = '—',
): string {
  if (code == null || code === '') return leer;
  return tabelle[code] ?? code;
}

// --- Sicherheitsstatus (verified | assumption | missing | klaerung) --------
//
// verified ist der beste, klaerung der schlechteste Fall - „bestätigt"
// transportiert das besser als „geprüft", das nach einer menschlichen
// Prüfung klänge, die hier nicht stattgefunden hat.
const sicherheitStatusTabelle: Record<string, string> = {
  verified: 'bestätigt',
  assumption: 'Annahme',
  missing: 'fehlt',
  klaerung: 'Klärung offen',
};

export const sicherheitStatus = (code: string | null | undefined): string =>
  nachschlagen(code, sicherheitStatusTabelle);

// --- Sicherungsseite (Bandseite | Bandgegenseite | keine | nicht bestimmbar)
const sicherungsseiteTabelle: Record<string, string> = {
  keine: 'keine (kein Zonenwechsel)',
  Bandseite: 'Bandseite',
  Bandgegenseite: 'Bandgegenseite',
  'nicht bestimmbar': 'nicht bestimmbar (gleiche Schutzstufe)',
};

export const sicherungsseite = (code: string | null | undefined): string =>
  nachschlagen(code, sicherungsseiteTabelle);

// --- Raumzuordnungsverfahren (Door.RaumVerfahren) ---------------------------
//
// Sagt nicht nur WOMIT, sondern WIE: Architekturtür gegen Ringsuche,
// beidseitig gegen innenliegend, eindeutig gegen mehrdeutig - „mehrdeutig"
// betrifft 800 von 1.852 Türen im Bestandsprojekt und muss als Einschränkung
// erkennbar sein, nicht als bloßes Etikett.
const raumVerfahrenTabelle: Record<string, string> = {
  revit: 'Architekturmodell (Revit-Räume)',
  arc_seiten: 'Architekturtür, beidseitig geprüft',
  arc_innen: 'Architekturtür, innenliegend',
  ring_innen: 'Ringsuche, innenliegend',
  ring_seiten: 'Ringsuche, beidseitig',
  ring_mehrdeutig: 'Ringsuche, mehrdeutig (mehr als zwei Räume)',
};

export const raumVerfahren = (code: string | null | undefined): string =>
  nachschlagen(code, raumVerfahrenTabelle);

// --- Türzustand (neu | bekannt | entfernt) ----------------------------------
const tuerStateTabelle: Record<string, string> = {
  neu: 'neu',
  bekannt: 'bekannt',
  entfernt: 'entfernt',
};

export const tuerState = (code: string | null | undefined): string =>
  nachschlagen(code, tuerStateTabelle);

// --- Ja/Nein -----------------------------------------------------------------
//
// Roh gespeichert als "TRUE"/"FALSE" (Tuerfeld-Auswahlwert dataType
// 'ja_nein'). Ein Wert, der weder TRUE noch FALSE ist, kommt unverändert
// zurück - er ist dann kein ja_nein-Feld, sondern freier Text.
export const jaNein = (roh: string | null | undefined): string => {
  if (roh === 'TRUE') return 'Ja';
  if (roh === 'FALSE') return 'Nein';
  if (roh == null || roh === '') return '—';
  return roh;
};

export const jaNeinBool = (wert: boolean | null | undefined): string =>
  wert === true ? 'Ja' : wert === false ? 'Nein' : '—';

// --- Merkmalsnamen des Sicherheitskonzepts ----------------------------------
//
// Dieselben 27 Feldnamen wie in TuerKlartext.cs (dort aus der privaten Klasse
// M der Ableitung gespiegelt, weil M privat ist und die Merkmalsnamen zur
// ANZEIGE gehören, nicht zur Ableitung).
const merkmalTabelle: Record<string, string> = {
  raum_bandseite: 'Raum Bandseite',
  raum_bandgegenseite: 'Raum Bandgegenseite',
  schutzzone_bandseite: 'Schutzzone Bandseite',
  schutzzone_bandgegenseite: 'Schutzzone Bandgegenseite',
  schutzstufe_bandseite: 'Schutzstufe Bandseite',
  schutzstufe_bandgegenseite: 'Schutzstufe Bandgegenseite',
  ist_zonenwechsel: 'Zonenwechsel',
  sicherungsseite: 'Sicherungsseite',
  tuerklasse: 'Türklasse',
  zuko_erforderlich: 'Zutrittskontrolle erforderlich',
  zuko_bauart: 'Zutrittskontrolle, Bauart',
  zuko_online: 'Zutrittskontrolle, online',
  oeffnungsueberwachung: 'Öffnungsüberwachung',
  verschlussueberwachung: 'Verschlussüberwachung',
  schloss_typ: 'Schlosstyp',
  beschlag_bandseite: 'Beschlag Bandseite',
  beschlag_bandgegenseite: 'Beschlag Bandgegenseite',
  fluchtweg: 'Fluchtweg',
  fluchtwegterminal: 'Fluchtwegterminal',
  kamera_erforderlich: 'Kamera erforderlich',
  kamera_qualitaet_ppm: 'Kamera-Qualität [ppm]',
  kamera_bauart: 'Kamera, Bauart',
  kamera_ausschluss: 'Kamera, Ausschluss',
  sprechstelle_aussen: 'Sprechstelle außen',
  sprechstelle_innen: 'Sprechstelle innen',
  glasbruchmelder: 'Glasbruchmelder',
  bewegungsmelder_raum: 'Bewegungsmelder Raum',
  stromversorgung: 'Stromversorgung',
  ableitung: 'Ableitung (bewusst keine Anforderung, R18)',
};

export const merkmal = (code: string): string => nachschlagen(code, merkmalTabelle, code);
